import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastProvider } from './components/ui/Toast';
import { isLoggedIn } from './services/auth';
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import ShareView from './pages/ShareView';
import AgentDashboard from './pages/AgentDashboard';
import CreateAgentWizard from './pages/CreateAgentWizard';
import AgentOverview from './pages/AgentOverview';
import AgentConfigCenter from './pages/AgentConfigCenter';
import AgentDeployments from './pages/AgentDeployments';
import InstanceOverview from './pages/InstanceOverview';
import DialogueTeaching from './pages/teaching/DialogueTeaching';
import DocumentTeaching from './pages/teaching/DocumentTeaching';
import KnowledgeBase from './pages/teaching/KnowledgeBase';
import PersonaConfig from './pages/teaching/PersonaConfig';
import ToolConfig from './pages/teaching/ToolConfig';
import CollabRoom from './pages/teaching/CollabRoom';
import EssenceView from './pages/EssenceView';
import TestingChat from './pages/TestingChat';
import LLMConfig from './pages/LLMConfig';
import Marketplace from './pages/Marketplace';
import Contracts from './pages/Contracts';
import TenantAdmin from './pages/TenantAdmin';
import UserAdmin from './pages/UserAdmin';
import TeachingCenter from './pages/TeachingCenter';
import MaterialLane from './pages/MaterialLane';
import ArtifactEditor from './pages/ArtifactEditor';
import ArtifactPlayground from './pages/ArtifactPlayground';
import TestingCenter from './pages/TestingCenter';
import GoalCenter from './pages/GoalCenter';
import AssetRuntimeOverview from './pages/AssetRuntimeOverview';
import ArchiveManager from './pages/ArchiveManager';
import Settings from './pages/Settings';
import Profile from './pages/Profile';

import ChannelAdmin from './pages/ChannelAdmin';
import DockerGCAdmin from './pages/DockerGCAdmin';
import SandboxAdmin from './pages/SandboxAdmin';
import WebhookAdmin from './pages/WebhookAdmin';
import Billing from './pages/Billing';

function AgentDeployRedirect() {
  const { agentId } = useParams();
  return <Navigate to={`/agents/${agentId}/deployments`} replace />;
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  return isLoggedIn() ? <>{children}</> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Login — no Layout, no auth required */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* 分享链接只读视图 — 公开路由，token 即凭证 */}
          <Route path="/share/:token" element={<ShareView />} />

          {/* All other routes — wrapped in Layout + require auth */}
          <Route
            path="/*"
            element={
              <AuthGuard>
                <Layout>
                  <Routes>
                    {/* Agent 管理主页 */}
                    <Route path="/" element={<AgentDashboard />} />

                    {/* 新建 Agent 向导 */}
                    <Route path="/agents/create" element={<CreateAgentWizard />} />

                    {/* Agent 概览 */}
                    <Route path="/agents/:agentId" element={<AgentOverview />} />

                    {/* LLM 配置 */}
                    <Route path="/agents/:agentId/config" element={<LLMConfig />} />

                    {/* 测试验证 */}
                    <Route path="/agents/:agentId/testing" element={<TestingChat />} />

                    {/* Essence 导出 */}
                    <Route path="/agents/:agentId/essences" element={<EssenceView />} />

                    {/* 配置中心 */}
                    <Route path="/agents/:agentId/config-center" element={<AgentConfigCenter />} />

                    {/* 运行时部署（新体系） */}
                    <Route path="/agents/:agentId/deployments" element={<AgentDeployments />} />

                    {/* 旧版文件部署入口收敛到运行时部署 */}
                    <Route path="/agents/:agentId/deploy" element={<AgentDeployRedirect />} />

                    {/* 实例运维 / 运行时大盘 */}
                    <Route path="/instances" element={<InstanceOverview />} />

                    {/* 归档中心 */}
                    <Route path="/archives" element={<ArchiveManager />} />

                    {/* 能力市场 */}
                    <Route path="/marketplace" element={<Marketplace />} />

                    {/* X→A 能力契约注册表 */}
                    <Route path="/contracts" element={<Contracts />} />

                    {/* 培训中心 */}
                    <Route path="/teaching" element={<TeachingCenter />} />

                    {/* 素材工坊（素材 → 产物 一键生成） */}
                    <Route path="/material-lane" element={<MaterialLane />} />

                    {/* 产物试玩面板（最小宿主验证 + 报告） */}
                    <Route path="/playground" element={<ArtifactPlayground />} />
                    <Route path="/playground/:artifactId" element={<ArtifactPlayground />} />

                    {/* 产物编辑器（编辑-改写闭环） */}
                    <Route path="/artifacts/:artifactId/edit" element={<ArtifactEditor />} />
                    <Route path="/testing" element={<TestingCenter />} />
                    <Route path="/agents/:agentId/goals" element={<GoalCenter />} />
                    <Route path="/channels" element={<Navigate to="/admin/channels" replace />} />

                    {/* 运行中心：旧版部署管理已收敛到运行时大盘 */}
                    <Route path="/deployments" element={<Navigate to="/instances" replace />} />
                    <Route path="/asset-runtimes" element={<AssetRuntimeOverview />} />
                    <Route path="/billing" element={<Billing />} />

                    {/* 系统设置 */}
                    <Route path="/settings" element={<Settings />} />

                    {/* 个人设置 */}
                    <Route path="/profile" element={<Profile />} />

                    {/* 管理页面 */}
                    <Route path="/admin/tenants" element={<TenantAdmin />} />
                    <Route path="/admin/users" element={<UserAdmin />} />
                    <Route path="/admin/channels" element={<ChannelAdmin />} />
                    <Route path="/admin/sandboxes" element={<SandboxAdmin />} />
                    <Route path="/admin/webhooks" element={<WebhookAdmin />} />
                    <Route path="/admin/gc" element={<DockerGCAdmin />} />

                    {/* 示教子路由 */}
                    <Route path="/teaching/collab" element={<CollabRoom />} />
                    <Route path="/agents/:agentId/teaching/dialogue" element={<DialogueTeaching />} />
                    <Route path="/agents/:agentId/teaching/documents" element={<DocumentTeaching />} />
                    <Route path="/agents/:agentId/teaching/knowledge" element={<KnowledgeBase />} />
                    <Route path="/agents/:agentId/teaching/persona" element={<PersonaConfig />} />
                    <Route path="/agents/:agentId/teaching/tools" element={<ToolConfig />} />

                    {/* 兼容旧路由 */}
                    <Route path="/projects/:projectId" element={<AgentOverview />} />
                    <Route path="/projects/:projectId/dialogue" element={<DialogueTeaching />} />
                    <Route path="/projects/:projectId/documents" element={<DocumentTeaching />} />
                    <Route path="/projects/:projectId/knowledge" element={<KnowledgeBase />} />
                    <Route path="/projects/:projectId/persona" element={<PersonaConfig />} />
                    <Route path="/projects/:projectId/essences" element={<EssenceView />} />
                  </Routes>
                </Layout>
              </AuthGuard>
            }
          />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
